import React, { useState, useEffect } from 'react';
import { ref, get, set, push, remove } from 'firebase/database';
import { database } from '../../firebase';

interface Admin {
  id: string;
  email: string;
}

export const AdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const adminsRef = ref(database, 'adminUsers');
      const snapshot = await get(adminsRef);
      if (snapshot.exists()) {
        setAdmins(Object.entries(snapshot.val()).map(([id, data]: [string, any]) => ({ id, ...data })));
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail) return;

    try {
      const adminsRef = ref(database, 'adminUsers');
      const newAdminRef = push(adminsRef);
      await set(newAdminRef, { email: newAdminEmail });
      setAdmins([...admins, { id: newAdminRef.key, email: newAdminEmail }]);
      setNewAdminEmail('');
    } catch (error) {
      console.error('Error adding admin:', error);
    }
  };

  const handleRemoveAdmin = async (adminId: string) => {
    if (!confirm('Are you sure you want to remove this admin?')) return;

    try {
      const adminRef = ref(database, `adminUsers/${adminId}`);
      await remove(adminRef);
      setAdmins(admins.filter(admin => admin.id !== adminId));
    } catch (error) {
      console.error('Error removing admin:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Management</h2>
      
      <form onSubmit={handleAddAdmin} className="mb-6 flex space-x-2">
        <input 
          type="email" 
          value={newAdminEmail} 
          onChange={(e) => setNewAdminEmail(e.target.value)} 
          placeholder="New admin email" 
          required 
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md">Add Admin</button>
      </form>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {admins.map(admin => (
            <div key={admin.id} className="px-6 py-4 flex justify-between items-center">
              <p className="text-sm font-medium text-gray-900">{admin.email}</p>
              <button onClick={() => handleRemoveAdmin(admin.id)} className="text-red-600 hover:underline">Remove</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};